import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import {
  User,
  Company,
  LoginCredentials,
  RegisterData,
  AuthContextState
} from '../types/auth';
import * as authService from '../services/authService';

/**
 * Auth Context
 *
 * Provides authentication state and methods throughout the application.
 */

// Create context with undefined default value
const AuthContext = createContext<AuthContextState | undefined>(undefined);

// Local storage keys
const TOKEN_KEY = 'nivoon_auth_token';
const USER_KEY = 'nivoon_user';
const COMPANY_KEY = 'nivoon_company';

/**
 * AuthProvider Props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 *
 * Wraps the application and provides authentication context.
 */
export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**
   * Initialize auth state from localStorage on mount
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);
        const storedCompany = localStorage.getItem(COMPANY_KEY);

        if (storedToken && storedUser) {
          // Parse stored data
          const parsedUser = JSON.parse(storedUser) as User;
          const parsedCompany = storedCompany ? (JSON.parse(storedCompany) as Company) : null;

          // Set axios default header
          authService.setAuthToken(storedToken);

          // Verify token is still valid by fetching profile
          try {
            const profileResponse = await authService.getProfile(storedToken);

            // Check trial expiration (exempt platform_admin)
            const profileUser = profileResponse.data.user;
            const profileCompany = profileResponse.data.company;
            if (
              profileUser.role !== 'platform_admin' &&
              profileCompany?.subscription_status === 'trial' &&
              profileCompany?.trial_ends_at &&
              new Date(profileCompany.trial_ends_at) < new Date()
            ) {
              handleLogout();
              window.location.href = '/trial-expired';
              return;
            }

            setUser(profileResponse.data.user);
            setCompany(profileResponse.data.company);
            setToken(storedToken);

            // Update stored data with latest from server
            localStorage.setItem(USER_KEY, JSON.stringify(profileResponse.data.user));
            if (profileResponse.data.company) {
              localStorage.setItem(COMPANY_KEY, JSON.stringify(profileResponse.data.company));
            }
          } catch (error) {
            // Token is invalid, clear auth state
            console.warn('Stored token is invalid, clearing auth state');
            handleLogout();
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        handleLogout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Force a clean logout when the server reports the session is no longer valid
   * (M4: the user was deactivated, deleted, demoted, or had their company
   * changed). Without this, a revoked user would just see repeated API errors.
   */
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error?.response?.status === 401 &&
          error?.response?.data?.code === 'session_invalidated'
        ) {
          try {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            localStorage.removeItem(COMPANY_KEY);
          } catch { /* ignore */ }
          authService.setAuthToken(null);
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  /**
   * Login user
   */
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      const response = await authService.login(credentials);

      const { user: loggedInUser, company: userCompany, token: authToken } = response.data;

      // Store auth data
      localStorage.setItem(TOKEN_KEY, authToken);
      localStorage.setItem(USER_KEY, JSON.stringify(loggedInUser));
      if (userCompany) {
        localStorage.setItem(COMPANY_KEY, JSON.stringify(userCompany));
      }

      // Set axios default header
      authService.setAuthToken(authToken);

      // Update state
      setUser(loggedInUser);
      setCompany(userCompany);
      setToken(authToken);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Register new user
   */
  const register = async (data: RegisterData): Promise<void> => {
    try {
      const response = await authService.register(data);

      const { user: newUser, company: newCompany, token: authToken } = response.data;

      // Store auth data
      localStorage.setItem(TOKEN_KEY, authToken);
      localStorage.setItem(USER_KEY, JSON.stringify(newUser));
      if (newCompany) {
        localStorage.setItem(COMPANY_KEY, JSON.stringify(newCompany));
      }

      // Set axios default header
      authService.setAuthToken(authToken);

      // Update state
      setUser(newUser);
      setCompany(newCompany);
      setToken(authToken);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Logout user
   */
  const logout = (): void => {
    handleLogout();
  };

  /**
   * Internal logout handler
   */
  const handleLogout = (): void => {
    // Call logout endpoint if token exists
    if (token) {
      authService.logout(token).catch(() => {
        // Ignore errors, we're logging out anyway
      });
    }

    // Clear local storage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(COMPANY_KEY);

    // Clear axios header
    authService.setAuthToken(null);

    // Clear state
    setUser(null);
    setCompany(null);
    setToken(null);
  };

  /**
   * Refresh user profile from server
   */
  const refreshProfile = async (): Promise<void> => {
    if (!token) {
      throw new Error('No authentication token available');
    }

    try {
      const profileResponse = await authService.getProfile(token);
      const { user: updatedUser, company: updatedCompany } = profileResponse.data;

      // Update stored data
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
      if (updatedCompany) {
        localStorage.setItem(COMPANY_KEY, JSON.stringify(updatedCompany));
      }

      // Update state
      setUser(updatedUser);
      setCompany(updatedCompany);
    } catch (error) {
      // If profile fetch fails, token might be invalid
      handleLogout();
      throw error;
    }
  };

  /**
   * Check if company name/subdomain is available
   */
  const checkCompanyAvailability = async (subdomain: string): Promise<boolean> => {
    try {
      return await authService.checkCompanyAvailability(subdomain);
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextState = {
    user,
    company,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    register,
    logout,
    refreshProfile,
    checkCompanyAvailability
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 *
 * Custom hook to access authentication context.
 * Must be used within AuthProvider.
 */
export function useAuth(): AuthContextState {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export default AuthContext;
